import { apiClient, safeApi, ApiResponse } from '../../utils/axiosWithAuth';

// =============================================
// TYPES for Billing V2 (Inventory-based)
// =============================================

export interface CategoryV2 {
  uuid: string;
  clientId: string;
  code: string;
  name: string;
  description?: string;
  image?: string;
  status: string;
}

export interface InventoryItemV2 {
  uuid: string;
  clientId: string;
  storeId: string;
  item_code: string;
  item_name: string;
  description?: string;
  categoryId?: string;
  categoryName?: string;
  selling_price: number;
  cost_price: number;
  tax: number;
  quantity_in_stock: number;
  product_image?: string;
  scale_unit?: string;
  status: string;
}

export interface CartItemV2 {
  inventoryItemId: string;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  originalPrice?: number;
  discount: number;
  tax: number;
  categoryId?: string;
  categoryName?: string;
  image?: string;
  description?: string;
  availableStock?: number;
}

export interface CartTotalsV2 {
  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  totalAmount: number;
  totalQuantity: number;
}

export interface DailySummaryV2 {
  totalOrders: number;
  totalRevenue: number;
  totalTax: number;
  totalDiscount: number;
  avgOrderAmount: number;
  totalBills: number;
}

export interface CreateOrderV2 {
  storeId?: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  items: {
    inventoryItemId: string;
    itemCode: string;
    itemName: string;
    quantity: number;
    unitPrice: number;
    discount?: number;
    tax?: number;
    categoryId?: string;
    categoryName?: string;
    image?: string;
  }[];
  paidAmount: number;
  paymentType: string;
  notes?: string;
  billDate?: string;
  taxRate?: number;
  isCreditSale?: boolean;
  creditDueDate?: string;
}

// =============================================
// API FUNCTIONS
// =============================================

export const fetchCategoriesV2API = () =>
  safeApi<CategoryV2[]>(() =>
    apiClient.get<ApiResponse<CategoryV2[]>>('/billing-v2/categories')
  );

export const fetchItemsV2API = (params: {
  categoryId?: string;
  search?: string;
  page?: number;
  limit?: number;
} = {}) =>
  safeApi<InventoryItemV2[]>(() => {
    const queryParams = new URLSearchParams();
    if (params.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    return apiClient.get<ApiResponse<InventoryItemV2[]>>(
      `/billing-v2/items${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    );
  });

export const fetchItemByIdV2API = (uuid: string) =>
  safeApi<InventoryItemV2>(() =>
    apiClient.get<ApiResponse<InventoryItemV2>>(`/billing-v2/items/${uuid}`)
  );

export const searchItemsV2API = (query: string, categoryId?: string) =>
  safeApi<InventoryItemV2[]>(() => {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);
    if (categoryId) queryParams.append('categoryId', categoryId);
    return apiClient.get<ApiResponse<InventoryItemV2[]>>(
      `/billing-v2/search?${queryParams.toString()}`
    );
  });

export const placeOrderV2API = (dto: CreateOrderV2) =>
  safeApi<{ bill: any; items: any[] }>(() =>
    apiClient.post<ApiResponse<{ bill: any; items: any[] }>>('/billing-v2/orders', dto)
  );

export const fetchDailySummaryV2API = (date?: string) =>
  safeApi<DailySummaryV2>(() => {
    const queryParams = date ? `?date=${date}` : '';
    return apiClient.get<ApiResponse<DailySummaryV2>>(`/billing-v2/reports/daily-summary${queryParams}`);
  });
